export interface ImageOptimizationResult {
  blob: Blob;
  width: number;
  height: number;
  originalSize: number;
  optimizedSize: number;
  format: string;
}

export interface ImageOptimizationOptions {
  targetWidth?: number;
  targetHeight?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  maintainAspectRatio?: boolean;
}

const DEFAULT_OPTIONS: Required<ImageOptimizationOptions> = {
  targetWidth: 1280,
  targetHeight: 720,
  quality: 0.85,
  format: 'webp',
  maintainAspectRatio: true,
};

/**
 * Load image from File/Blob
 */
export const loadImage = (file: File | Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validate image dimensions and aspect ratio
 */
export const validateImageDimensions = (
  width: number,
  height: number
): { valid: boolean; aspectRatio: number; error?: string } => {
  const aspectRatio = width / height;
  const target16x9 = 16 / 9;
  const tolerance = 0.05; // 5% tolerance

  const MIN_WIDTH = 854;
  const MIN_HEIGHT = 480;
  const MAX_WIDTH = 1920;
  const MAX_HEIGHT = 1080;

  if (width < MIN_WIDTH || height < MIN_HEIGHT) {
    return {
      valid: false,
      aspectRatio,
      error: `Image too small. Minimum: ${MIN_WIDTH}×${MIN_HEIGHT}px`,
    };
  }

  if (width > MAX_WIDTH || height > MAX_HEIGHT) {
    return {
      valid: false,
      aspectRatio,
      error: `Image too large. Maximum: ${MAX_WIDTH}×${MAX_HEIGHT}px`,
    };
  }

  if (Math.abs(aspectRatio - target16x9) > tolerance) {
    return {
      valid: false,
      aspectRatio,
      error: `Image must be 16:9 aspect ratio. Current: ${aspectRatio.toFixed(2)}`,
    };
  }

  return { valid: true, aspectRatio };
};

/**
 * Optimize and resize image with center crop to 16:9 ratio
 */
export const optimizeImage = async (
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<ImageOptimizationResult> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  // Load original image
  const img = await loadImage(file);
  const originalSize = file.size;

  // Create canvas for processing
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Set target dimensions (16:9 ratio)
  canvas.width = opts.targetWidth;
  canvas.height = opts.targetHeight;

  // Calculate crop dimensions to maintain 16:9 and center the image
  const targetRatio = opts.targetWidth / opts.targetHeight;
  const sourceRatio = img.width / img.height;

  let sx = 0;
  let sy = 0;
  let sWidth = img.width;
  let sHeight = img.height;

  if (sourceRatio > targetRatio) {
    // Source is wider - crop width
    sWidth = img.height * targetRatio;
    sx = (img.width - sWidth) / 2;
  } else if (sourceRatio < targetRatio) {
    // Source is taller - crop height
    sHeight = img.width / targetRatio;
    sy = (img.height - sHeight) / 2;
  }

  // Draw cropped and scaled image
  ctx.drawImage(
    img,
    sx, sy, sWidth, sHeight,
    0, 0, canvas.width, canvas.height
  );

  // Convert to blob with compression
  const mimeType = opts.format === 'webp' ? 'image/webp' : 
                   opts.format === 'png' ? 'image/png' : 'image/jpeg';
  
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error('Failed to create blob'));
      },
      mimeType,
      opts.quality
    );
  });

  return {
    blob,
    width: canvas.width,
    height: canvas.height,
    originalSize,
    optimizedSize: blob.size,
    format: opts.format,
  };
};

/**
 * Validate file before processing
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB before optimization

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload JPG, PNG, or WebP.',
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File too large. Maximum 10MB before optimization.',
    };
  }

  return { valid: true };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
